import get from 'lodash/get';
import { currentSubscriptionStatus } from '../utils/subscription/subscriptionStatus';
import { mustBeLoggedIn } from '../utils/form/paymentValidation';
import fetchProducts from '../utils/connectors/fetchProducts';
import { statuses as couponStatuses } from '../constants/subscription/coupon';
import unsubscribe from '../constants/analytics/categoryActionLabel/unsubscribe';
import { logUnsubscribeActivity } from './logging';
import { validateLocation, setCountryAndState } from './location';
import unWrapSubscriptionError from './utils/unWrapSubscriptionError';
import getDisabledCacheConfig from './utils/getDisabledCacheConfig';
import getCountryCode from './utils/getCountryConfig';

export const GET_SIGNATURE = 'GET_SIGNATURE';
export const CREATE_SUBSCRIPTION = 'CREATE_SUBSCRIPTION';
export const GET_SUBSCRIPTION = 'GET_SUBSCRIPTION';
export const CANCEL_SUBSCRIPTION = 'CANCEL_SUBSCRIPTION';
export const RENEW_SUBSCRIPTION = 'RENEW_SUBSCRIPTION';
export const GET_PAYMENT_INFORMATION = 'GET_PAYMENT_INFORMATION';
export const UPDATE_PAYMENT_INFORMATION = 'UPDATE_PAYMENT_INFORMATION';
export const FETCH_COUPON = 'FETCH_COUPON';

const transform = ({ data }) => ({ content: data });

function getSubscriptionEffect() {
  return (dispatch, getState) => {
    const state = getState();

    return dispatch({
      type: GET_SUBSCRIPTION,
      api: {
        endpoint: ['subscription', 'getSubscription'],
        transform,
        args: [
          {
            countryCode: getCountryCode(state),
            noCache: getDisabledCacheConfig(state),
          },
        ],
      },
    });
  };
}

function cancelSubscriptionEffect() {
  return {
    type: CANCEL_SUBSCRIPTION,
    api: {
      endpoint: ['subscription', 'cancelSubscription'],
      args: [{}],
    },
  };
}

export function createTuneinSubscription(
  currency,
  token,
  productSku,
  source,
  couponCode,
) {
  return {
    type: CREATE_SUBSCRIPTION,
    api: {
      endpoint: ['subscription', 'createSubscription'],
      transform,
      args: [
        {
          currency,
          token,
          source,
          productSku,
          couponCode,
        },
      ],
    },
    meta: {
      contentId: 'tuneinSubscription',
    },
  };
}

export function renewTuneinSubscription(
  currency,
  token,
  productSku,
  source,
  couponCode,
) {
  return {
    type: RENEW_SUBSCRIPTION,
    api: {
      endpoint: ['subscription', 'renewSubscription'],
      transform,
      args: [
        {
          currency,
          token,
          productSku,
          source,
          couponCode,
        },
      ],
    },
    meta: {
      contentId: 'tuneinSubscription',
    },
  };
}

export function getPaymentInformationEffect() {
  return {
    type: GET_PAYMENT_INFORMATION,
    api: {
      endpoint: ['subscription', 'getPaymentInformation'],
      transform,
      args: [{}],
    },
    meta: {
      contentId: 'tuneinSubscription',
    },
  };
}

export function updatePaymentInformationEffect(token) {
  return {
    type: UPDATE_PAYMENT_INFORMATION,
    api: {
      endpoint: ['subscription', 'updatePaymentInformation'],
      transform,
      args: [{ token }],
    },
    meta: {
      contentId: 'tuneinSubscription',
    },
  };
}

export function createAccount(
  currency,
  token,
  upsellReferrer,
  productSku,
  couponCode,
) {
  return async (dispatch, getState) => {
    const { id } = get(getState(), 'me.details', {});
    if (!id) {
      throw new Error(mustBeLoggedIn);
    }
    await dispatch(
      createTuneinSubscription(
        currency,
        token,
        productSku,
        upsellReferrer,
        couponCode,
      ),
    ).catch(unWrapSubscriptionError);
  };
}

export function renewSubscription(
  currency,
  token,
  productSku,
  upsellReferrer,
  couponCode,
) {
  return async (dispatch, getState) => {
    const { id } = get(getState(), 'me.details', {});
    if (!id) {
      throw new Error(mustBeLoggedIn);
    }
    await dispatch(
      renewTuneinSubscription(
        currency,
        token,
        productSku,
        upsellReferrer,
        couponCode,
      ),
    ).catch(unWrapSubscriptionError);
  };
}

export function getSubscription() {
  return (dispatch) => dispatch(getSubscriptionEffect());
}

export function getPaymentInformation() {
  return async (dispatch, getState) => {
    const { id } = get(getState(), 'me.details', {});
    if (!id) {
      throw new Error(mustBeLoggedIn);
    }
    return await dispatch(getPaymentInformationEffect());
  };
}

export function updatePaymentInformation(token) {
  return async (dispatch, getState) => {
    const { id } = get(getState(), 'me.details', {});
    if (!id) {
      throw new Error(mustBeLoggedIn);
    }
    return await dispatch(updatePaymentInformationEffect(token)).catch(
      unWrapSubscriptionError,
    );
  };
}

export function cancelSubscription(location) {
  return async (dispatch, getState) => {
    const { current } = getState().subscription;

    if (current && current.status === currentSubscriptionStatus.canceled) {
      return null;
    }

    const label =
      current && current.billingPeriod
        ? unsubscribe.labels[current.billingPeriod.toLowerCase()]
        : '';
    await dispatch(cancelSubscriptionEffect());
    dispatch(fetchProducts(location, true));
    return dispatch(logUnsubscribeActivity(unsubscribe.actions.confirm, label));
  };
}

export function updateLocationPaymentInformation(updatedZip, country, state) {
  return async (dispatch, getState) => {
    const previousZip = get(getState(), 'subscription.paymentInfo.postalCode');

    const shouldLookUpZip = !get(
      getState(),
      'subscription.paymentInfo.enableManualSelection',
    );
    if (shouldLookUpZip || previousZip !== updatedZip) {
      await dispatch(validateLocation(updatedZip));
    } else {
      await dispatch(setCountryAndState({ country, state }));
    }
  };
}

export function fetchCoupon(couponCode, status) {
  return {
    type: FETCH_COUPON,
    api: {
      endpoint: ['subscription', 'lookupCoupon'],
      args: [couponCode, status],
      registerAppError: false,
    },
  };
}

export function validateActiveCoupon(couponCode) {
  return fetchCoupon(couponCode, couponStatuses.redeemable);
}

export function fetchActiveCouponIfNeeded(couponCode) {
  return (dispatch, getState) => {
    const couponCodeInStore = get(getState(), 'subscription.coupon.code');

    if (couponCode !== couponCodeInStore) {
      return dispatch(fetchCoupon(couponCode, couponStatuses.redeemable));
    }

    // calling then on this function so must resolve the promise
    return Promise.resolve();
  };
}

/*
  see connectors folder for connector action creators:

  fetchSubscriptionIfNeeded
  fetchCouponForRoute
*/
